/* ─────────────────────────────────────────────────────────────────────────
   dashboardV3 — data hooks (REAL endpoints)

   useSensorList()  → categorises /user/v2/sensor_list into water / pumps /
                      borewells / valves (same predicates as DashboardV2).
   useWaterStates() → light per-tank status pass to ORDER the water page.
   useHubDerived()  → collapsed hub: one pass over the list to compute
                      per-category online/offline, Sense (water-sense) devices,
                      valve open/closed, and the needs-attention alert list.
                      Uses the same endpoints/thresholds the cards use, so the
                      numbers match DashboardV2 (which also counts water-sense
                      and solenoids as separate devices).
   ──────────────────────────────────────────────────────────────────────── */
import axios from "axios";
import { useContext, useEffect, useState, useCallback } from "react";
import { GlobalState } from "../../GlobalState";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";

const API = process.env.REACT_APP_API;

const isPump     = (s) => s?.motor_id && !s.isBorewell;
const isBorewell = (s) => s?.motor_id && s.isBorewell;
const isValve    = (s) => s?.type === "Solenoid Valve" && s?.solenoid_id;
const isWater    = (s) => !s?.motor_id && s?.type !== "Gateway" && s?.type !== "Solenoid Valve";

function toBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") { const x = v.trim().toLowerCase(); return x === "1" || x === "true" || x === "on"; }
  return Boolean(v);
}

export function useSensorList() {
  const state = useContext(GlobalState);
  const [loading, setLoading] = useState(true);
  const [water, setWater]         = useState([]);
  const [pumps, setPumps]         = useState([]);
  const [borewells, setBorewells] = useState([]);
  const [valves, setValves]       = useState([]);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/v2/sensor_list`, { headers: { Authorization: state.token[0] } });
      const all = (res.data || []).flatMap((g) => g.sensors || []);
      setWater(all.filter(isWater).map((s) => ({ sensor_id: s.sensor_id, type: s.type, name: s.location_name })));
      setPumps(all.filter(isPump).map((s) => ({ motor_id: s.motor_id, sensor_id: s.sensor_id, isBorewell: false })));
      setBorewells(all.filter(isBorewell).map((s) => ({ motor_id: s.motor_id, sensor_id: s.sensor_id, isBorewell: true })));
      setValves(all.filter(isValve).map((s) => ({
        solenoid_id: s.solenoid_id, location_name: s.location_name,
        sensor_online: s.sensor_online, valve_on: s.valve_on, updatedAt: s.updatedAt,
      })));
    } catch (err) {
      console.log("sensor_list:", err.message);
    } finally {
      setLoading(false);
    }
  }, [state.token]);

  useEffect(() => { fetchList(); }, [fetchList]);
  return { loading, water, pumps, borewells, valves, refresh: fetchList };
}

/* ── Per-sensor state fetchers ────────────────────────────────────────────── */
async function fetchWaterState(token, s) {
  try {
    const [readingRes, dataRes] = await Promise.all([
      axios.get(`${API}/sensor/reading?sensor_id=${s.sensor_id}&type=${s.type}`, { headers: { Authorization: token } }),
      axios.get(`${API}/sensor/data?sensor_id=${s.sensor_id}`, { headers: { Authorization: token } }).catch(() => ({ data: {} })),
    ]);
    const d = readingRes.data || {};
    const min = parseInt(dataRes.data?.lower_limit) || 20;
    const max = parseInt(dataRes.data?.upper_limit) || 80;
    const level = parseFloat(d.volume || 0);
    const faulty  = d.raw_data === -1;
    const online  = d.sensor_status === "online";
    const offline = !faulty && !online;
    let kind = null;
    if (offline) kind = "offline";
    else if (faulty) kind = "faulty";
    else if (level > 100) kind = "overflow";
    else if (level > max) kind = "high";
    else if (level < min) kind = "low";
    return {
      ...s, category: "water", online, offline, kind, level, min, max,
      since: offline ? formatOfflineDuration(d.date, d.time) : null,
      name: d.location_name || s.name,
    };
  } catch {
    return { ...s, category: "water", online: false, offline: true, kind: "offline", level: 0, since: null, name: s.name };
  }
}

async function fetchPumpState(token, p) {
  try {
    const res = await axios.get(`${API}/sensor/v2/pump_status?motor_id=${p.motor_id}`, { headers: { Authorization: token } });
    const online = res.data?.sensor_status === "online";
    const running = toBool(res.data?.motor_status?.motor_on);
    const name = res.data?.motor_status?.location_name || (p.isBorewell ? "Borewell" : "Pump");
    const since = online ? null : formatOfflineDuration(res.data?.motor_status?.updatedAt);

    let sense = null;
    if (p.isBorewell) {
      try {
        const r = await axios.get(`${API}/sensor/reading?sensor_id=${p.sensor_id}&type=water-sense`, { headers: { Authorization: token } });
        const w = r.data || {};
        if (w?.name === "water-sense" && !w?.isDeleted) {
          const senseOnline = w.sensor_status === "online";
          sense = {
            category: "sense", sensor_id: p.sensor_id, motor_id: p.motor_id, name,
            online: senseOnline, wet: toBool(w.value),
            kind: senseOnline ? null : "offline",
            since: senseOnline ? null : formatOfflineDuration(w.date, w.time),
          };
        }
      } catch { /* no water-sense */ }
    }
    return { ...p, category: p.isBorewell ? "borewell" : "pump", online, running, since, name, kind: online ? null : "offline", sense };
  } catch {
    return { ...p, category: p.isBorewell ? "borewell" : "pump", online: false, running: false, since: null, kind: "offline", sense: null };
  }
}

async function fetchValveState(token, v) {
  try {
    const res = await axios.get(`${API}/sensor/solenoid?solenoid_id=${v.solenoid_id}`, { headers: { Authorization: token } });
    const d = res.data?.data || {};
    const online = toBool(d.sensor_online ?? v.sensor_online);
    const open = toBool(d.valve_on ?? v.valve_on);
    return {
      category: "valve", solenoid_id: v.solenoid_id, name: d.location_name || v.location_name,
      online, open, kind: online ? null : "offline",
      since: online ? null : formatOfflineDuration(d.updatedAt || v.updatedAt),
    };
  } catch {
    const online = toBool(v.sensor_online);
    return { category: "valve", solenoid_id: v.solenoid_id, name: v.location_name, online, open: toBool(v.valve_on), kind: online ? null : "offline", since: null };
  }
}

export function useWaterStates(water, ready) {
  const state = useContext(GlobalState);
  const [map, setMap] = useState({});
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    (async () => {
      const token = state.token[0];
      const states = await Promise.all(water.map((s) => fetchWaterState(token, s)));
      if (!alive) return;
      const m = {};
      states.forEach((s) => { m[s.sensor_id] = s; });
      setMap(m);
    })();
    return () => { alive = false; };
  }, [ready, water, state.token]);
  return map;
}

/* Borewells that actually carry a water-sense (HydroSense) device.
   Used by the Sense page so it lists ONLY sense-enabled borewells. */
export function useSenseBorewells(borewells, parentReady) {
  const state = useContext(GlobalState);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!parentReady) return;
    let alive = true;
    (async () => {
      if (!borewells || borewells.length === 0) { if (alive) { setList([]); setLoading(false); } return; }
      const token = state.token[0];
      const results = await Promise.all(borewells.map(async (b) => {
        try {
          const r = await axios.get(`${API}/sensor/reading?sensor_id=${b.sensor_id}&type=water-sense`, { headers: { Authorization: token } });
          const w = r.data || {};
          return (w?.name === "water-sense" && !w?.isDeleted) ? b : null;
        } catch { return null; }
      }));
      if (!alive) return;
      setList(results.filter(Boolean));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [parentReady, borewells, state.token]);
  return { loading, list };
}

/* ── Hub derivation ──────────────────────────────────────────────────────── */
export function useHubDerived({ water, pumps, borewells, valves, ready, refreshMs = 20000 }) {
  const state = useContext(GlobalState);
  const [loading, setLoading] = useState(true);
  const [derived, setDerived] = useState({
    counts: { total: 0, online: 0, offline: 0 },
    alerts: [],
    waterStats: { total: 0, online: 0, offline: 0, overflow: 0, high: 0, low: 0 },
    pbStats: { total: 0, online: 0, offline: 0, motorsRunning: 0, borewellsRunning: 0 },
    senseStats: { total: 0, online: 0, offline: 0, wet: 0, dry: 0 },
    valveStats: { total: 0, online: 0, offline: 0, open: 0, closed: 0 },
  });

  useEffect(() => {
    if (!ready) return;
    let alive = true;

    const run = async () => {
      const token = state.token[0];
      const [waterStates, pumpStates, boreStates, valveStates] = await Promise.all([
        Promise.all(water.map((s) => fetchWaterState(token, s))),
        Promise.all(pumps.map((p) => fetchPumpState(token, p))),
        Promise.all(borewells.map((b) => fetchPumpState(token, b))),
        Promise.all((valves || []).map((v) => fetchValveState(token, v))),
      ]);
      if (!alive) return;

      const senseDevices = boreStates.map((b) => b.sense).filter(Boolean);
      const base = [...waterStates, ...pumpStates, ...boreStates, ...valveStates];
      const total   = base.length + senseDevices.length;
      const online  = base.filter((x) => x.online).length + senseDevices.filter((s) => s.online).length;
      const offline = total - online;

      const alerts = [
        ...waterStates.filter((x) => x.kind),
        ...pumpStates.filter((x) => x.kind),
        ...boreStates.filter((x) => x.kind),
        ...senseDevices.filter((s) => s.kind),
        ...valveStates.filter((v) => v.kind),
      ];

      const wOnline = waterStates.filter((x) => x.online).length;
      const pbOnline = pumpStates.filter((x) => x.online).length + boreStates.filter((x) => x.online).length;
      const sOnline = senseDevices.filter((s) => s.online).length;
      const vOnline = valveStates.filter((v) => v.online).length;

      setDerived({
        counts: { total, online, offline },
        alerts,
        waterStats: {
          total: waterStates.length, online: wOnline, offline: waterStates.length - wOnline,
          overflow: waterStates.filter((x) => x.kind === "overflow").length,
          high: waterStates.filter((x) => x.kind === "high").length,
          low: waterStates.filter((x) => x.kind === "low").length,
        },
        pbStats: {
          total: pumpStates.length + boreStates.length, online: pbOnline,
          offline: (pumpStates.length + boreStates.length) - pbOnline,
          motorsRunning: pumpStates.filter((x) => x.running).length,
          borewellsRunning: boreStates.filter((x) => x.running).length,
        },
        senseStats: {
          total: senseDevices.length, online: sOnline, offline: senseDevices.length - sOnline,
          wet: senseDevices.filter((s) => s.wet).length,
          dry: senseDevices.filter((s) => s.online && !s.wet).length,
        },
        valveStats: {
          total: valveStates.length, online: vOnline, offline: valveStates.length - vOnline,
          open: valveStates.filter((v) => v.online && v.open).length,
          closed: valveStates.filter((v) => v.online && !v.open).length,
        },
      });
      setLoading(false);
    };

    run();
    const id = setInterval(run, refreshMs);   // auto-refresh the hub
    return () => { alive = false; clearInterval(id); };
  }, [ready, water, pumps, borewells, valves, state.token, refreshMs]);

  return { loading, ...derived };
}
